// client/src/lib/url-state.ts — a boolean filter that survives being typed into the address bar ·
// OAT-003 (04 §9.2.3).
//
// THE TRAP, found by OAT-003 and recorded as D-1197. §9.2.3 makes every list filter a
// Zod-validated search param so that „a filtered ledger is shareable". TanStack Router's default
// parser is `parseSearchWith(JSON.parse)`, so the SHARED url `?isCapex=true` reaches the schema
// as the BOOLEAN `true` — while the same filter set by clicking the control reaches it as the
// STRING `'true'`, because `stringifySearchWith(JSON.stringify)` writes a string value with
// quotes and parses it back to a string. A schema that admits only one of the two rejects the
// other, and `validateSearch` failing renders the route's error boundary: the whole screen is
// replaced by a Zod dump. Clicking works, sharing does not, and nothing about the URL looks
// wrong.
//
// So the three tri-state filters in the app admit BOTH spellings and read them through
// `urlBoolean` below. Neither `z.coerce.boolean()` nor a `.transform()` is available as a fix:
// the first maps the string „false" to `true`, which would make the „nu" option unreachable, and
// the second makes the schema's input and output types differ, which TanStack Router's
// `navigate({ search })` reducer refuses — it writes the INPUT shape and reads back the output.

import { z } from 'zod';

/**
 * A tri-state URL filter: absent, on, or off — spelled as a boolean when the URL was typed and
 * as a string when the control was clicked. Both are accepted; `urlBoolean` is how it is read.
 */
export const zUrlBoolean = z.union([z.boolean(), z.enum(['true', 'false'])]);

export type UrlBoolean = z.infer<typeof zUrlBoolean>;

/** `true` / `'true'` → true; `false` / `'false'` → false; absent → `undefined`. */
export function urlBoolean(value: UrlBoolean | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  return value === true || value === 'true';
}

/**
 * The same value as a `<select>`'s `value` — `''` for „all", which is the option a tri-state
 * filter opens on. A raw boolean bound to `value` is a type error and, before that, a select
 * that shows nothing selected.
 */
export function urlBooleanOption(value: UrlBoolean | undefined): '' | 'true' | 'false' {
  const parsed = urlBoolean(value);
  if (parsed === undefined) return '';
  return parsed ? 'true' : 'false';
}
